import React, { PureComponent, Suspense } from 'react';
import { Redirect } from 'react-router-dom';
import { Button } from 'Common/components/Atoms/Button';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { Div } from 'Common/components/Atoms/Div';
import { Col, Flex } from 'Common/components/Atoms/Flex';
import { FontIcon } from 'Common/components/Atoms/FontIcon';
import { InputField } from 'Common/components/Atoms/InputField';
import { Link } from 'Common/components/Atoms/Link';
import { Loading } from 'Common/components/Atoms/Loading';
import { Modal } from 'Common/components/Molecules/Modal';
import { Text } from 'Common/components/Atoms/Text';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import IneligibleCandidate from '../../../components/IneligibleCandidate';
import { faChevronDown, faExclamation } from '@fortawesome/pro-solid-svg-icons';
import { Theme } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import withTheme from '@mui/styles/withTheme';
import { actions as signupsActions } from '../../../reducers/signups';
import { actions as accountActions } from '../../../reducers/Account';
import { accountSelector, isLoadingAccountSelector } from 'selectors/Account';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import history from '../../../history';
import { retry } from '../../../utils/retry';
import { User } from '../../../models/User';
import format from 'utils/formatting/numbers';
import { Justification } from './Justification';
import { Testimonials, scrollIdSelector } from './Testimonials';
import { FAQs } from './FAQs';
import { Footer } from './Footer';
import { LANDING } from 'DisplayText/ReviewerOnboarding';
import { PageContainer } from 'Common/components/Templates/PageContainer';
import landingImage from './assets/landing.png';

const styles = (theme: Theme) => ({
  becomeReviewerSection: {
    background: `url('${landingImage}')`,
    backgroundColor: theme.palette.primary.light,
    backgroundSize: 'cover',
    height: 568,
    [theme.breakpoints.down('md')]: {
      height: 650,
    },
  },
  overview: {
    marginTop: 'auto',
    marginBottom: 'auto',
    paddingLeft: 24,
    paddingRight: 24,
    width: 470,
    height: 320,
    alignSelf: 'flex-start',
    [theme.breakpoints.down('md')]: {
      marginTop: 16,
      marginBottom: 16,
      textAlign: 'center',
      width: 500,
      height: 200,
    },
  },
  application: {
    height: 375,
    width: 343,
    paddingLeft: 24,
    paddingRight: 24,
    marginTop: 'auto',
    marginBottom: 'auto',
    justifyContent: 'space-evenly',
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
      width: '55%',
      marginBottom: 16,
    },
  },
  scroller: {
    flexBasis: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      visibility: 'hidden',
    },
  },
  applicationInputLabel: {
    color: theme.palette.common.white,
  },
  applicationInput: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
  },
  applicationHelperText: {
    backgroundColor: theme.palette.error.main,
    color: `${theme.palette.common.white} !important`,
    margin: 0,
    marginTop: -6,
    paddingTop: 6,
    paddingLeft: 6,
    paddingRight: 6,
    padding: 2,
    borderRadius: theme.shape.borderRadius,
  },
  radio: {
    display: 'none',
    '& + label': {
      display: 'inline-block',
      borderRadius: theme.shape.borderRadius,
      background: theme.palette.grey[200],
      cursor: 'pointer',
      '&:not(:checked + label):hover': {
        background: theme.palette.grey[300],
      },
    },
    '&:checked + label': {
      background: theme.palette.primary.main,
      '& p': {
        color: theme.palette.common.white,
      },
    },
  },
});

const MIN_PASSWORD_LENGTH = 8;

interface FieldProps {
  isLoading: boolean;
  children: React.ReactNode;
}

const FormField = (props: FieldProps): JSX.Element => {
  const { isLoading, children } = props;
  return (
    <Div>
      {isLoading ? <ContentLoader height="38px" width="100%" /> : children}
    </Div>
  );
};

const PasswordStrengthBar = React.lazy(() =>
  retry(() => import('react-password-strength-bar'))
);

export interface Props {
  error?: string;
  reviewerSignUp: (
    name: string,
    email: string,
    password: string,
    phone: string
  ) => void;
  classes: {
    [x: string]: string;
  };
  theme: Theme;
  isLoadingAccount: boolean;
  user: User | null;
}

interface State {
  name: string;
  email: string;
  password: string;
  showUsModal: boolean;
  showAccountModal: boolean;
  usIp: boolean;
  usBased: boolean;
  errors: {
    name?: boolean;
    email?: boolean;
    password?: boolean;
    pwnd?: boolean;
  };
  ineligible: boolean;
  showPassword: boolean;
}

export class LandingPageComponent extends PureComponent<Props, State> {
  state: State = {
    name: '',
    email: '',
    password: '',
    showUsModal: false,
    showAccountModal: false,
    usIp: true,
    usBased: true,
    errors: {},
    ineligible: false,
    showPassword: false,
  };

  componentDidMount() {
    fetch('https://get.geojs.io/v1/ip/country.json')
      .then((res) => res.json())
      .then((data) => {
        if (data.country !== 'US') {
          this.setState({
            usIp: false,
          });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  componentDidUpdate(prevProps: Props) {
    const { error } = this.props;
    if (!prevProps.error && error && error === 'USER_ALREADY_EXISTS') {
      this.setState({ showAccountModal: true });
    }
    if (
      !prevProps.error &&
      error &&
      error ===
        'This password may have previously been breached on another service, please pick another.'
    ) {
      this.setState({ errors: { ...this.state.errors, pwnd: true } });
    }
  }

  handleContinueClick = () => {
    const { usIp } = this.state;
    if (!usIp) {
      this.setState({ showUsModal: true });
    } else {
      this.submit();
    }
  };

  handleUsModalButtonClick = () => {
    const { usBased } = this.state;
    if (usBased) {
      this.setState({ showUsModal: false }, this.submit);
    } else {
      this.setState({ showUsModal: false, ineligible: true });
    }
  };

  submit = () => {
    const { reviewerSignUp } = this.props;
    const { name, email, password } = this.state;
    const errors = {
      name: !name,
      password: !password || password.length < MIN_PASSWORD_LENGTH,
      email: !email,
    };

    if (Object.values(errors).every((o) => !o)) {
      reviewerSignUp(name, email, password, '');
    } else {
      this.setState({ errors: errors });
    }
  };

  renderUsModal(): JSX.Element {
    const { showUsModal, usBased } = this.state;
    const { classes, theme } = this.props;
    return (
      <Modal
        id="non-US-modal"
        width={460}
        height={295}
        icon={faExclamation}
        primaryIconColor={theme.palette.highlight.main}
        secondaryIconColor={theme.palette.highlight.dim}
        variant="alert"
        title={LANDING.NON_US_MODAL_TITLE}
        isOpen={showUsModal}
        onRequestClose={() => this.setState({ showUsModal: false })}
        primaryButtonLabel={LANDING.NON_US_MODAL_BUTTON_LABEL}
        onClickPrimaryButton={this.handleUsModalButtonClick}
      >
        <Text variant="body1" mb={4}>
          {LANDING.NON_US_MODAL_PROMPT}
        </Text>
        <Div mb={1}>
          <input
            className={classes.radio}
            data-testid="qa-radio-us"
            type="radio"
            id="us-based"
            name="us-based"
            checked={usBased}
            onChange={() => this.handleChange('usBased', false)}
          />
          <label id="us-based-label" htmlFor="us-based">
            <Text py={1} px={1.5} variant="h4">
              {LANDING.NON_US_MODAL_YES}
            </Text>
          </label>
        </Div>
        <Div mb={1}>
          <input
            className={classes.radio}
            data-testid="qa-radio-non-us"
            type="radio"
            id="non-us-based"
            name="us-based"
            checked={!usBased}
            onChange={(event) => this.handleChange('usBased', false)}
          />
          <label id="non-us-based-label" htmlFor="non-us-based">
            <Text
              py={1}
              px={1.5}
              variant="body2"
              fontWeight={theme.typography.fontWeightSemiBold}
            >
              {LANDING.NON_US_MODAL_NO}
            </Text>
          </label>
        </Div>
      </Modal>
    );
  }

  renderAccountModal() {
    const { showAccountModal } = this.state;
    return (
      <Modal
        id="account-modal"
        width={460}
        variant="generic"
        title={LANDING.EXISTING_MODAL_TITLE}
        isOpen={showAccountModal}
        onRequestClose={() => this.setState({ showAccountModal: false })}
        primaryButtonLabel={LANDING.EXISTING_MODAL_BUTTON_LABEL}
        onClickPrimaryButton={this.handleAccountModalClick}
      >
        <Text variant="body1">{LANDING.EXISTING_MODAL_TEXT}</Text>
      </Modal>
    );
  }

  handleAccountModalClick = () => {
    history.push('/login');
  };

  handleChange = (name: keyof State, value: string | boolean) => {
    const { errors } = this.state;
    this.setState({
      [name]: value,
      errors: { ...errors, [name]: undefined },
    } as Pick<State, keyof State>);
  };

  render(): JSX.Element {
    const { classes, theme, isLoadingAccount, user } = this.props;
    const { errors, ineligible, name, email, password, showPassword } =
      this.state;

    if (user?.email_verified) {
      return <Redirect to="/signups/reviewer/profile" />;
    }

    if (ineligible) {
      return (
        <PageContainer width="500px">
          <IneligibleCandidate />
        </PageContainer>
      );
    }

    const passwordError = errors?.password;
    const pwndError = errors?.pwnd;

    return (
      <>
        <Flex
          className={classes.becomeReviewerSection}
          justifyContent="center"
          alignItems="center"
          width="100%"
          flexWrap="wrap"
        >
          <Col className={classes.overview}>
            <Text
              variant="display1"
              fontSize={42}
              color={theme.palette.common.white}
              mb={4}
            >
              {LANDING.TITLE}
            </Text>
            <Text variant="body1" color={theme.palette.common.white}>
              {LANDING.DESCRIPTION}
            </Text>
          </Col>
          <Col className={classes.application}>
            <FormField isLoading={isLoadingAccount}>
              <InputField
                id="name"
                inputLabelProps={{
                  className: classes.applicationInputLabel,
                }}
                inputProps={{
                  className: classes.applicationInput,
                }}
                formHelperTextProps={{
                  className: classes.applicationHelperText,
                }}
                label={LANDING.NAME_LABEL}
                placeholder={LANDING.NAME_PLACEHOLDER}
                data-testid="qa-name"
                value={name || ''}
                width="100%"
                onChange={(event) =>
                  this.handleChange('name', event.target.value)
                }
                error={errors && errors.name}
                helperText={errors && errors.name && LANDING.NAME_ERROR}
                autoComplete="off"
              />
            </FormField>
            <FormField isLoading={isLoadingAccount}>
              <InputField
                id="email"
                inputLabelProps={{
                  className: classes.applicationInputLabel,
                }}
                inputProps={{
                  className: classes.applicationInput,
                }}
                formHelperTextProps={{
                  className: classes.applicationHelperText,
                }}
                label={LANDING.EMAIL_LABEL}
                placeholder={LANDING.EMAIL_PLACEHOLDER}
                data-testid="qa-email"
                value={email || ''}
                width="100%"
                onChange={(event) =>
                  this.handleChange('email', event.target.value)
                }
                error={errors && errors.email}
                helperText={errors && errors.email && LANDING.EMAIL_ERROR}
                autoComplete="off"
              />
            </FormField>
            <FormField isLoading={isLoadingAccount}>
              <InputField
                id="password"
                inputLabelProps={{
                  className: classes.applicationInputLabel,
                }}
                inputProps={{
                  className: classes.applicationInput,
                }}
                formHelperTextProps={{
                  className: classes.applicationHelperText,
                }}
                data-testid="qa-password"
                label={LANDING.PASSWORD_LABEL}
                placeholder={LANDING.PASSWORD_PLACEHOLDER.replace(
                  '{NUM_CHARACTERS}',
                  format(MIN_PASSWORD_LENGTH).as('integer')
                )}
                type={showPassword ? 'text' : 'password'}
                width="100%"
                onChange={(event) =>
                  this.handleChange('password', event.target.value)
                }
                error={passwordError || pwndError}
                endIcon={
                  <FontIcon
                    icon={showPassword ? faEyeSlash : faEye}
                    color={theme.palette.common.white}
                    onClick={() =>
                      this.setState({ showPassword: !showPassword })
                    }
                    cursor="pointer"
                    size="sm"
                  />
                }
                helperText={
                  passwordError
                    ? LANDING.PASSWORD_ERROR.replace(
                        '{NUM_CHARACTERS}',
                        format(MIN_PASSWORD_LENGTH).as('integer')
                      )
                    : pwndError
                    ? LANDING.PWND_ERROR
                    : null
                }
                autoComplete="off"
              />
              <Suspense
                fallback={
                  password ? (
                    <Div height={26}>
                      <ContentLoader height="2px" width="100%" />
                    </Div>
                  ) : null
                }
              >
                {password && (
                  <PasswordStrengthBar password={password} minLength={8} />
                )}
              </Suspense>
            </FormField>
            <Button
              data-testid="landing-page__submit-button"
              variant="contained"
              color="secondary"
              onClick={this.handleContinueClick}
            >
              {isLoadingAccount ? (
                <Flex
                  height={24}
                  width={24}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Loading size={24} customcolor={theme.palette.common.white} />
                </Flex>
              ) : (
                'Continue'
              )}
            </Button>
            <Text
              variant="body1"
              color={theme.palette.common.white}
              textAlign="center"
            >
              Already a PullRequest Reviewer? <Link href="/login">Sign in</Link>
            </Text>
          </Col>
          <Flex className={classes.scroller}>
            <Link href={scrollIdSelector} textDecoration="none">
              <Col center>
                <Text
                  variant="body1"
                  color={theme.palette.common.white}
                  textAlign="center"
                >
                  {LANDING.LEARN_MORE}
                </Text>
                <FontIcon
                  size="2x"
                  icon={faChevronDown}
                  color={theme.palette.grey[200]}
                />
              </Col>
            </Link>
          </Flex>
        </Flex>
        {this.renderUsModal()}
        {this.renderAccountModal()}
        <Testimonials />
        <Justification />
        <FAQs />
        <Footer />
      </>
    );
  }
}

function mapStateToProps(state: any) {
  const { signups } = state;
  const account = accountSelector(state);
  const isLoadingAccount = isLoadingAccountSelector(state);
  return {
    isLoadingAccount,
    user: account.user,
    error: signups.error,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({ ...signupsActions, ...accountActions }, dispatch);
}

export default withTheme<Theme, React.JSXElementConstructor<Props>>(
  withStyles(styles)(
    connect(mapStateToProps, mapDispatchToProps)(LandingPageComponent)
  )
);
